import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  RefreshCw, RotateCcw, AlertCircle, CheckCircle2, Clock, Loader2,
  Zap, Users, UserMinus, Shield, ShieldOff, Camera, Fingerprint,
  ChevronDown, MonitorCheck, ChevronLeft, ChevronRight, Trash2,
} from 'lucide-react'

const PAGE_SIZE = 50
import { toast } from 'sonner'
import { getSyncStats, getSyncJobs, triggerSync, retrySync, clearDoneJobs, clearSupersededJobs, type SyncDeviceStat, type SyncJob } from '@/api/sync'
import { getRejectedPhotoUsers, type RejectedPhotoUser } from '@/api/users'
import { ApiError } from '@/lib/api-error'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

// ── Task 1.3: Operation labels ────────────────────────────────────────────────

const OPERATION_LABELS: Record<string, string> = {
  ENROLL_USER_TO_DEVICE:  'Registrar en dispositivo',
  GRANT_ACCESS:           'Otorgar acceso',
  REVOKE_ACCESS:          'Revocar acceso',
  BLOCK_USER_ON_DEVICE:   'Bloquear usuario',
  UNBLOCK_USER_ON_DEVICE: 'Desbloquear usuario',
  CREATE_USER:            'Crear usuario',
  UPDATE_USER:            'Actualizar usuario',
  DELETE_USER:            'Eliminar usuario',
  UPLOAD_FACE:            'Subir foto',
  UPLOAD_FINGERPRINT:     'Subir huella',
  DELETE_FINGERPRINT:     'Eliminar huella',
  CREATE_FACE_RECORD:     'Registro facial',
  DELETE_FACE_RECORD:     'Eliminar registro facial',
  SYNC_FACE_LIBRARY:      'Sincronizar biblioteca facial',
  UPDATE_SCHEDULE:        'Actualizar horario de acceso',
}

// ── Task 1.1: Relative timestamps ─────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60)   return `hace ${diff} seg`
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`
  return new Date(iso).toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

// ── Task 1.2: Operation icons ─────────────────────────────────────────────────

const OP_ICON_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  UPLOAD_FACE:            { icon: <Camera      className="w-3.5 h-3.5" />, color: 'bg-purple-500/10 text-purple-500' },
  CREATE_FACE_RECORD:     { icon: <Camera      className="w-3.5 h-3.5" />, color: 'bg-purple-500/10 text-purple-500' },
  DELETE_FACE_RECORD:     { icon: <Camera      className="w-3.5 h-3.5" />, color: 'bg-slate-500/10 text-slate-400'   },
  SYNC_FACE_LIBRARY:      { icon: <Camera      className="w-3.5 h-3.5" />, color: 'bg-purple-500/10 text-purple-500' },
  UPLOAD_FINGERPRINT:     { icon: <Fingerprint className="w-3.5 h-3.5" />, color: 'bg-blue-500/10 text-blue-500'     },
  DELETE_FINGERPRINT:     { icon: <Fingerprint className="w-3.5 h-3.5" />, color: 'bg-slate-500/10 text-slate-400'   },
  DELETE_USER:            { icon: <UserMinus   className="w-3.5 h-3.5" />, color: 'bg-red-500/10 text-red-400'       },
  REVOKE_ACCESS:          { icon: <ShieldOff   className="w-3.5 h-3.5" />, color: 'bg-red-500/10 text-red-400'       },
  BLOCK_USER_ON_DEVICE:   { icon: <ShieldOff   className="w-3.5 h-3.5" />, color: 'bg-amber-500/10 text-amber-500'   },
  GRANT_ACCESS:           { icon: <Shield      className="w-3.5 h-3.5" />, color: 'bg-emerald-500/10 text-emerald-500'},
  UPDATE_SCHEDULE:        { icon: <Shield      className="w-3.5 h-3.5" />, color: 'bg-emerald-500/10 text-emerald-500'},
  CREATE_USER:            { icon: <Users       className="w-3.5 h-3.5" />, color: 'bg-accent/10 text-accent'         },
  UPDATE_USER:            { icon: <Users       className="w-3.5 h-3.5" />, color: 'bg-accent/10 text-accent'         },
  ENROLL_USER_TO_DEVICE:  { icon: <Users       className="w-3.5 h-3.5" />, color: 'bg-accent/10 text-accent'         },
  UNBLOCK_USER_ON_DEVICE: { icon: <Users       className="w-3.5 h-3.5" />, color: 'bg-emerald-500/10 text-emerald-500'},
}

function operationIcon(op: string): { icon: React.ReactNode; color: string } {
  return OP_ICON_CONFIG[op] ?? { icon: <RefreshCw className="w-3.5 h-3.5" />, color: 'bg-border text-text-muted' }
}

// ── Task 8: Plain-language explanation for jobs that need manual attention ─────
// These errors are permanent — the device rejects the same data every time, so
// retrying alone never resolves them. Someone has to fix the underlying data.

interface ErrorExplanation { title: string; why: string; action: string }

function explainError(lastError: string | undefined): ErrorExplanation | null {
  if (!lastError) return null
  const msg = lastError.toLowerCase()
  if (msg.includes('badjsoncontent:usertype')) {
    return {
      title: 'Este dispositivo no acepta la categoría de este usuario',
      why: 'El tipo de usuario que tiene asignado no es una de las categorías que este modelo de dispositivo reconoce. Por eso lo rechaza cada vez que se intenta subir.',
      action: 'Entra a la ficha del usuario y cambia su categoría a una compatible (Miembro regular, Visitante o Lista restringida), luego reintenta.',
    }
  }
  if (msg.includes('badjsoncontent:employeeno')) {
    return {
      title: 'El número de identificación no es válido para este dispositivo',
      why: 'El dispositivo solo acepta números en el campo de identificación, y el valor guardado incluye letras u otros caracteres.',
      action: 'Corrige el número de identificación del usuario (solo dígitos) desde su ficha, luego reintenta.',
    }
  }
  if (msg.includes('subpicanalysismodelingerror')) {
    return {
      title: 'La foto no pasó el control de calidad facial del dispositivo',
      why: 'El dispositivo analiza la foto para asegurarse de reconocer el rostro con claridad. La puede rechazar si está borrosa, mal iluminada, en ángulo, o si no detecta un rostro.',
      action: 'Sube una nueva foto de frente, bien iluminada y sin accesorios que tapen el rostro, desde la ficha del usuario.',
    }
  }
  if (msg.includes('badjsoncontent')) {
    return {
      title: 'El dispositivo rechazó un dato de este usuario',
      why: 'Alguno de los datos enviados no tiene un formato que el dispositivo acepte.',
      action: 'Revisa la ficha del usuario y corrige el dato señalado en el detalle técnico de abajo, luego reintenta.',
    }
  }
  return null
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: SyncJob['status'] }) {
  const styles: Record<string, string> = {
    pending:         'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    processing:      'bg-blue-500/10  text-blue-600  dark:text-blue-400  border-blue-500/20',
    done:            'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    failed:          'bg-red-500/10   text-red-600   dark:text-red-400   border-red-500/20',
    needs_attention: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
  }
  const labels: Record<string, string> = {
    pending: 'Pendiente', processing: 'Procesando', done: 'Completado', failed: 'Fallido',
    needs_attention: 'Necesita atención',
  }
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${styles[status] ?? ''}`}>
      {labels[status] ?? status}
    </span>
  )
}

// ── Task 2: HealthBanner ──────────────────────────────────────────────────────

function HealthBanner({
  totalPending,
  totalFailed,
  totalNeedsAttention,
  totalProcessing,
  onRetryAll,
  loading,
}: {
  totalPending: number
  totalFailed: number
  totalNeedsAttention: number
  totalProcessing: number
  onRetryAll: () => void
  loading: boolean
}) {
  if (loading) return null

  if (totalNeedsAttention > 0) {
    return (
      <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-2xl">
        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
        <p className="text-amber-600 dark:text-amber-400 text-sm">
          <span className="font-semibold">{totalNeedsAttention} usuario{totalNeedsAttention !== 1 ? 's' : ''}</span>
          {' '}necesita{totalNeedsAttention !== 1 ? 'n' : ''} corrección de datos — revisa el filtro "Atención" abajo.
        </p>
      </div>
    )
  }

  if (totalFailed > 0) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-500/5 border border-red-500/20 rounded-2xl">
        <div className="flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-red-600 dark:text-red-400 text-sm">
            <span className="font-semibold">{totalFailed} job{totalFailed !== 1 ? 's' : ''}</span>
            {' '}fallaron después de 5 intentos — se requiere acción manual.
          </p>
        </div>
        <button
          onClick={onRetryAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 border border-red-500/30 hover:border-red-500/60 rounded-xl transition-colors shrink-0"
        >
          <RotateCcw className="w-3 h-3" />
          Reintentar todos ({totalFailed})
        </button>
      </div>
    )
  }

  if (totalPending > 0 || totalProcessing > 0) {
    return (
      <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-2xl">
        <Clock className="w-4 h-4 text-amber-500 shrink-0" />
        <p className="text-amber-600 dark:text-amber-400 text-sm">
          <span className="font-semibold">{totalPending + totalProcessing} job{(totalPending + totalProcessing) !== 1 ? 's' : ''}</span>
          {' '}en cola — la sincronización se ejecuta automáticamente cada 30 seg.
        </p>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2.5 px-4 py-3 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
      <p className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">
        Todo sincronizado — los dispositivos están al día.
      </p>
    </div>
  )
}

// ── RejectedPhotosBanner: members whose photo the device has rejected ─────────
// These are members whose photo the device has actually rejected
// (SubpicAnalysisModelingError) on a real sync attempt — not a guess from
// image dimensions, since plenty of otherwise-odd-looking photos still pass
// the device's face check fine. They'll keep failing until someone re-uploads
// a fresh photo from the member's profile.

function RejectedPhotosBanner() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<RejectedPhotoUser[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    getRejectedPhotoUsers()
      .then(r => setUsers(r.users))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading || users.length === 0) return null

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-2xl">
        <div className="flex items-center gap-2.5">
          <Camera className="w-4 h-4 text-amber-500 shrink-0" />
          <p className="text-amber-600 dark:text-amber-400 text-sm">
            <span className="font-semibold">{users.length} miembro{users.length !== 1 ? 's' : ''}</span>
            {' '}tiene{users.length !== 1 ? 'n' : ''} una foto que el dispositivo rechazó — hay que resubirla.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:border-amber-500/60 rounded-xl transition-colors shrink-0"
        >
          Ver lista
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader>
            <DialogTitle>Fotos pendientes de resubir</DialogTitle>
          </DialogHeader>
          <p className="text-text-muted text-xs -mt-2">
            Entra al perfil de cada uno y sube una foto nueva desde su foto de perfil. Esta lista se va achicando sola a medida que las resubes.
          </p>
          <ul className="flex flex-col gap-1 max-h-80 overflow-y-auto">
            {users.map(u => (
              <li key={u.id}>
                <button
                  onClick={() => navigate(`/members/${u.id}`)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-background transition-colors text-left"
                >
                  <span className="text-text-primary text-sm truncate">{u.name}</span>
                  <span className="text-text-muted text-xs shrink-0">#{u.employee_no}</span>
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ── Task 3: DeviceCard ────────────────────────────────────────────────────────

const STATUS_DOT: Record<string, string> = {
  online:  'bg-emerald-500',
  offline: 'bg-slate-400',
  locked:  'bg-amber-500',
}

function DeviceCardSkeleton() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3 animate-pulse">
      <div className="flex items-center gap-2.5">
        <Skeleton className="w-2 h-2 rounded-full" />
        <Skeleton className="h-3.5 w-28 rounded" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[0,1,2,3].map(i => <Skeleton key={i} className="h-8 rounded-lg" />)}
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
    </div>
  )
}

function DeviceCard({
  stat,
  onRetry,
}: {
  stat: SyncDeviceStat
  onRetry: (deviceId: string) => void
}) {
  const total = stat.pending + stat.processing + stat.done + stat.failed
  const pct = (n: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '0%'

  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3 hover:border-accent/20 transition-colors">
      {/* Name + status dot */}
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[stat.device_status] ?? 'bg-slate-400'}`} />
        <p className="text-text-primary text-sm font-semibold truncate">{stat.device_name}</p>
      </div>
      <p className="text-text-muted text-[10px] -mt-2">
        {stat.device_status === 'online' ? 'En línea' : 'Sin conexión'}
      </p>

      {total === 0 ? (
        <p className="text-text-muted text-xs text-center py-1">Sin actividad reciente</p>
      ) : (
        <>
          {/* 4 counters */}
          <div className="grid grid-cols-4 gap-1 text-center">
            {[
              { label: 'Pendiente',  value: stat.pending,    color: 'text-amber-500'  },
              { label: 'Procesando', value: stat.processing, color: 'text-blue-500'   },
              { label: 'Completado', value: stat.done,       color: 'text-emerald-500'},
              { label: 'Fallido',    value: stat.failed,     color: 'text-red-500'    },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <span className={`text-base font-black tabular-nums leading-none ${color}`}>{value}</span>
                <span className="text-[9px] text-text-muted leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* Segmented progress bar */}
          <div className="h-1.5 bg-background rounded-full overflow-hidden flex">
            {stat.pending    > 0 && <div className="bg-amber-400 h-full"   style={{ width: pct(stat.pending)    }} />}
            {stat.processing > 0 && <div className="bg-blue-400 h-full"    style={{ width: pct(stat.processing) }} />}
            {stat.done       > 0 && <div className="bg-emerald-400 h-full" style={{ width: pct(stat.done)       }} />}
            {stat.failed     > 0 && <div className="bg-red-400 h-full"     style={{ width: pct(stat.failed)     }} />}
          </div>
        </>
      )}

      {/* Retry button only when there are failures */}
      {stat.failed > 0 && (
        <button
          onClick={() => onRetry(stat.device_id)}
          className="flex items-center justify-center gap-1.5 text-xs text-red-500 border border-red-500/20 hover:border-red-500/40 hover:bg-red-500/5 rounded-xl py-1.5 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Reintentar {stat.failed} fallido{stat.failed !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  )
}

// ── Task 4: JobItem ───────────────────────────────────────────────────────────

function JobItemSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3 animate-pulse">
      <Skeleton className="w-7 h-7 rounded-xl shrink-0" />
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-3 w-20 rounded" />
        </div>
        <Skeleton className="h-2.5 w-24 rounded" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  )
}

function JobItem({ job, onRetry }: { job: SyncJob; onRetry: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const navigate = useNavigate()
  const { icon, color } = operationIcon(job.operation)
  const needsAttention = job.status === 'needs_attention'
  const explanation = needsAttention ? explainError(job.last_error) : null

  return (
    <div className="px-4 py-3 flex flex-col gap-1.5">
      <div className="flex items-start gap-3">
        {/* Operation icon */}
        <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
          {icon}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {job.member_name ? (
            <>
              <p className="text-text-primary text-sm font-bold truncate">{job.member_name}</p>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-text-muted text-[10px] font-semibold">{OPERATION_LABELS[job.operation] ?? job.operation}</span>
                <span className="text-text-muted text-[10px]">· {job.device_name}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-text-primary text-xs font-semibold">
                {OPERATION_LABELS[job.operation] ?? job.operation}
              </span>
              <span className="text-text-muted text-[10px]">{job.device_name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-text-muted text-[10px]">
              {job.attempts} intento{job.attempts !== 1 ? 's' : ''} · {relativeTime(job.created_at)}
            </span>
            {(job.status === 'failed' || needsAttention) && (
              <button
                onClick={() => onRetry(job.id)}
                className="text-[10px] text-accent hover:underline font-medium"
              >
                Reintentar
              </button>
            )}
            {needsAttention && job.member_id && (
              <button
                onClick={() => navigate(`/members/${job.member_id}`)}
                className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline font-semibold"
              >
                Editar usuario →
              </button>
            )}
          </div>
        </div>

        {/* Right side: status badge + error toggle (only for plain failures — needs_attention shows its explanation inline) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge status={job.status} />
          {job.last_error && !needsAttention && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="text-text-muted hover:text-red-400 transition-colors"
              aria-expanded={expanded}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Needs-attention: plain-language explanation, always visible (not behind a toggle) */}
      {needsAttention && job.last_error && (
        <div className="ml-10 bg-amber-500/5 border border-amber-500/20 border-l-2 border-l-amber-500 rounded-r-lg p-2.5">
          {explanation ? (
            <>
              <p className="text-amber-600 dark:text-amber-400 text-xs font-bold">{explanation.title}</p>
              <p className="text-text-muted text-[11px] mt-1.5 leading-relaxed">{explanation.why}</p>
              <p className="text-text-muted text-[9px] font-bold uppercase tracking-wide mt-2.5">Qué hacer</p>
              <p className="text-text-primary text-[11px] leading-relaxed">{explanation.action}</p>
              <p className="font-mono text-[9px] text-text-muted/70 mt-2.5 break-all">Detalle técnico: {job.last_error}</p>
            </>
          ) : (
            <p className="font-mono text-[10px] text-amber-600 dark:text-amber-400 break-all leading-relaxed">
              {job.last_error}
            </p>
          )}
        </div>
      )}

      {/* Plain failures: raw error behind the chevron toggle, as before */}
      {expanded && job.last_error && !needsAttention && (
        <div className="ml-10 bg-red-500/5 border border-red-500/15 rounded-lg p-2.5">
          <p className="font-mono text-[10px] text-red-600 dark:text-red-400 break-all leading-relaxed">
            {job.last_error}
          </p>
        </div>
      )}
    </div>
  )
}

// ── Task 6: Filter type ───────────────────────────────────────────────────────

type StatusFilter = 'all' | 'pending' | 'failed' | 'needs_attention' | 'done'

const FILTER_LABELS: Record<StatusFilter, string> = {
  all: 'Todos', pending: 'Pendientes', failed: 'Fallidos', needs_attention: 'Atención', done: 'Completados',
}

// ── Main SyncPage ─────────────────────────────────────────────────────────────

export function SyncPage() {
  const [stats,       setStats]       = useState<SyncDeviceStat[]>([])
  const [jobs,        setJobs]        = useState<SyncJob[]>([])
  const [total,       setTotal]       = useState(0)
  const [page,        setPage]        = useState(1)
  const [filter,      setFilter]      = useState<StatusFilter>('all')
  const [loading,     setLoading]     = useState(true)
  const [triggering,  setTriggering]  = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [clearingDone, setClearingDone] = useState(false)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  const [clearingSuperseded, setClearingSuperseded] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const load = useCallback(async (currentPage = 1, currentFilter: StatusFilter = 'all') => {
    try {
      const offset = (currentPage - 1) * PAGE_SIZE
      const [statsRes, jobsRes] = await Promise.all([
        getSyncStats(),
        getSyncJobs({
          limit:  PAGE_SIZE,
          offset,
          ...(currentFilter !== 'all' ? { status: currentFilter } : {}),
        }),
      ])
      setStats(statsRes.stats)
      setJobs(jobsRes.jobs)
      setTotal(jobsRes.total)
      setLastRefresh(new Date())
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) {
        toast.error('No se puede conectar con el proxy local')
      } else {
        toast.error('Error al cargar estado de sincronización')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(page, filter)
    const id = setInterval(() => load(page, filter), 10_000)
    return () => clearInterval(id)
  }, [load, page, filter])

  function handleFilterChange(f: StatusFilter) {
    setFilter(f)
    setPage(1)
  }

  async function handleTrigger() {
    setTriggering(true)
    try {
      await triggerSync()
      toast.success('Sincronización iniciada')
      setTimeout(() => load(page, filter), 1500)
    } catch {
      toast.error('Error al iniciar sincronización')
    } finally {
      setTriggering(false)
    }
  }

  async function handleRetryDevice(deviceId: string) {
    try {
      await retrySync({ deviceId })
      toast.success('Jobs reiniciados')
      setTimeout(() => load(page, filter), 1000)
    } catch {
      toast.error('Error al reintentar')
    }
  }

  async function handleRetryJob(jobId: string) {
    try {
      await retrySync({ jobId })
      toast.success('Job reiniciado')
      setTimeout(() => load(page, filter), 1000)
    } catch {
      toast.error('Error al reintentar')
    }
  }

  async function handleClearDone() {
    setClearingDone(true)
    try {
      const r = await clearDoneJobs()
      toast.success(`${r.deleted} job${r.deleted !== 1 ? 's' : ''} completado${r.deleted !== 1 ? 's' : ''} eliminado${r.deleted !== 1 ? 's' : ''}`)
      setConfirmClearOpen(false)
      setPage(1)
      load(1, filter)
    } catch {
      toast.error('Error al limpiar el historial')
    } finally {
      setClearingDone(false)
    }
  }

  async function handleClearSuperseded() {
    setClearingSuperseded(true)
    try {
      const r = await clearSupersededJobs()
      toast.success(r.deleted > 0
        ? `${r.deleted} intento${r.deleted !== 1 ? 's' : ''} viejo${r.deleted !== 1 ? 's' : ''} eliminado${r.deleted !== 1 ? 's' : ''} (ya superado${r.deleted !== 1 ? 's' : ''} por uno exitoso)`
        : 'No había intentos viejos superados para limpiar')
      setPage(1)
      load(1, filter)
    } catch {
      toast.error('Error al limpiar los intentos superados')
    } finally {
      setClearingSuperseded(false)
    }
  }

  async function handleRetryAll() {
    try {
      await retrySync()
      toast.success('Todos los jobs fallidos reiniciados')
      setTimeout(() => load(1, filter), 1000)
      setPage(1)
    } catch {
      toast.error('Error al reintentar')
    }
  }

  const totalFailed         = stats.reduce((s, d) => s + d.failed,     0)
  const totalNeedsAttention = stats.reduce((s, d) => s + d.needs_attention, 0)
  const totalPending        = stats.reduce((s, d) => s + d.pending,    0)
  const totalProcessing     = stats.reduce((s, d) => s + d.processing, 0)
  // Counts from device stats (accurate across all pages)
  const jobCounts: Record<StatusFilter, number> = {
    all:             stats.reduce((s, d) => s + d.pending + d.processing + d.done + d.failed + d.needs_attention, 0),
    pending:         stats.reduce((s, d) => s + d.pending, 0),
    failed:          stats.reduce((s, d) => s + d.failed,  0),
    needs_attention: stats.reduce((s, d) => s + d.needs_attention, 0),
    done:            stats.reduce((s, d) => s + d.done,    0),
  }

  return (
    <div className="flex flex-col gap-5">

      {/* ── Task 5: Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-text-primary font-semibold text-lg">Sincronización</h1>
            {/* Task 5.1: Live processing indicator */}
            {totalProcessing > 0 && (
              <span className="flex items-center gap-1.5 text-emerald-500 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Procesando...
              </span>
            )}
          </div>
          {/* Task 5.3: Last refresh timestamp */}
          {lastRefresh && (
            <p className="text-text-muted text-[10px] mt-0.5">
              Actualizado {lastRefresh.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          )}
        </div>

        {/* Task 5.2: Action buttons hierarchy */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => load(page, filter)}
            className="p-1.5 text-text-muted hover:text-text-primary hover:bg-border/50 rounded-lg transition-colors"
            title="Actualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleTrigger}
            disabled={triggering}
            className="flex items-center gap-1.5 px-3 py-2 bg-accent text-white text-sm font-medium rounded-xl hover:bg-accent/90 disabled:opacity-50 transition-colors shadow-sm"
          >
            {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Forzar Sync
          </button>
        </div>
      </div>

      {/* ── Task 2: HealthBanner ── */}
      <HealthBanner
        totalPending={totalPending}
        totalFailed={totalFailed}
        totalNeedsAttention={totalNeedsAttention}
        totalProcessing={totalProcessing}
        onRetryAll={handleRetryAll}
        loading={loading}
      />

      <RejectedPhotosBanner />

      {/* ── Task 3 + 7.1: Device cards section ── */}
      <section className="flex flex-col gap-3">
        <h2 className="text-text-muted text-xs font-semibold uppercase tracking-widest">
          Dispositivos
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <DeviceCardSkeleton />
            <DeviceCardSkeleton />
          </div>
        ) : stats.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-8 text-center">
            <MonitorCheck className="w-8 h-8 text-text-muted mx-auto mb-2" />
            <p className="text-text-muted text-sm">No hay dispositivos registrados.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.map(stat => (
              <DeviceCard
                key={stat.device_id}
                stat={stat}
                onRetry={handleRetryDevice}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Task 4 + 6 + 7.2: Job feed section ── */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-text-muted text-xs font-semibold uppercase tracking-widest">
            Historial de jobs
          </h2>

          <div className="flex items-center gap-2">
          {jobCounts.needs_attention > 0 && (
            <button
              onClick={handleClearSuperseded}
              disabled={clearingSuperseded}
              className="flex items-center gap-1 text-[10px] text-text-muted hover:text-red-500 transition-colors px-2 py-1 rounded-lg hover:bg-red-500/5 disabled:opacity-60"
              title="Elimina intentos en 'Necesita atención' cuyo mismo cambio ya se sincronizó bien después (quedaron como duplicados viejos)"
            >
              {clearingSuperseded ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              Limpiar duplicados superados
            </button>
          )}
          {jobCounts.done > 0 && (
            <button
              onClick={() => setConfirmClearOpen(true)}
              className="flex items-center gap-1 text-[10px] text-text-muted hover:text-red-500 transition-colors px-2 py-1 rounded-lg hover:bg-red-500/5"
              title="Elimina los jobs completados del historial — pendientes, fallidos y atención se quedan"
            >
              <Trash2 className="w-3 h-3" />
              Limpiar completados
            </button>
          )}
          {/* Task 6: Filter pills with integrated count */}
          <div className="flex gap-1">
            {(['all', 'pending', 'failed', 'needs_attention', 'done'] as StatusFilter[]).map(f => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                className={`px-2.5 py-1 text-xs rounded-xl border transition-colors ${
                  filter === f
                    ? 'bg-accent/10 text-accent border-accent/30'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                {FILTER_LABELS[f]}
                <span className="ml-1 text-[9px] opacity-70">· {jobCounts[f]}</span>
              </button>
            ))}
          </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="divide-y divide-border">
              <JobItemSkeleton />
              <JobItemSkeleton />
              <JobItemSkeleton />
              <JobItemSkeleton />
            </div>
          ) : jobs.length === 0 ? (
            /* Task 7.3: Empty state differentiated by filter */
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              <p className="text-text-muted text-sm">
                {filter === 'all'             ? 'Sin jobs registrados.' :
                 filter === 'pending'         ? 'No hay jobs pendientes.' :
                 filter === 'failed'          ? 'No hay jobs fallidos.' :
                 filter === 'needs_attention' ? 'Ningún usuario necesita atención.' :
                                                'No hay jobs completados.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {jobs.map(job => (
                <JobItem key={job.id} job={job} onRetry={handleRetryJob} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-4 px-1">
            <p className="text-text-muted text-xs">
              Mostrando {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de{' '}
              <span className="font-semibold text-text-primary">{total}</span> jobs
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed hover:bg-border/50 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-text-muted px-2 tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed hover:bg-border/50 rounded-lg transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Confirmar limpieza de completados ── */}
      <Dialog open={confirmClearOpen} onOpenChange={o => !o && setConfirmClearOpen(false)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader><DialogTitle>Limpiar historial</DialogTitle></DialogHeader>
          <p className="text-text-muted text-sm mt-2">
            ¿Eliminar los <strong className="text-text-primary">{jobCounts.done}</strong> job{jobCounts.done !== 1 ? 's' : ''} completado{jobCounts.done !== 1 ? 's' : ''} del historial? Los pendientes, fallidos y que necesitan atención no se tocan. Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="mt-4">
            <button onClick={() => setConfirmClearOpen(false)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancelar</button>
            <button
              onClick={handleClearDone}
              disabled={clearingDone}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {clearingDone && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Eliminar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
